/**
 * Spec 136 — Accounting portal surface.
 *
 * Nav shows My Queue and their own bills register. Everything else opens from a
 * queue task deep-link — never as a browsable list page.
 *
 * ★ WIDENED 2026-08-03 (PO) to admit `/accounting`.
 *
 * The original "inbox only" rule was right for what existed at the time: every
 * other list was somebody else's job — the invoice list is the PM's APPROVAL
 * queue, and handing accounting a page full of decisions they cannot make is
 * what got them removed from the invoice pool in the first place.
 *
 * What changed is that accounting's OWN work became representable. A bill being
 * charged out has a state now, so there is finally a register that answers
 * their question rather than a PM's, and the queue alone could not carry it:
 * a queue is what is on you right now, and they also need to find the bill
 * charged last month. Admitting one page, still not the PM's lists.
 *
 * ★ NARROWED 2026-08-04 (PO ruling): *"Accounting should not review work order /
 * whole turn / inspection."*
 *
 * `/work-orders/:id`, `/inspections/:id/walk` and `/inspections/:id/campaign`
 * are GONE from this allow-list. They were admitted as "provenance" — somewhere
 * to go and read the paper behind a bill — but what they actually open is
 * somebody else's whole job, full of controls accounting may not touch
 * (approve, dispute, dispatch, re-tag, edit a finding) and none of which the
 * contract lets them use (spec 145 §4).
 *
 * The provenance did not disappear; it moved INTO their own surface. The two
 * task pages admitted below — `/accounting/turns/:id` and
 * `/accounting/work-orders/:id` — carry the work, the bills, what the
 * automation already did, and the documents, read-only, on the page where the
 * charge is recorded. So a task is finished without ever leaving accounting's
 * three pages.
 *
 * `/dispositions/:id` STAYS: the deposit letter is accounting's own document —
 * they mark it sent, tick its five steps and close it (spec 145 §1).
 */

const UUID =
  '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';

/** Sidebar keys Accounting may see — their inbox and their own register. */
export const ACCOUNTING_NAV_KEYS = new Set(['/my-queue', '/accounting']);

/**
 * ★ THE REGISTER PAGE IS THE ACCOUNTING SEAT'S ONLY (PO, 2026-08-12: *"All
 * bills — page visible only to accounting department, hide it for others"*).
 *
 * This reverses the audience half of the 2026-08-03 widening: back then the
 * page was ALSO given to Admin and PM ("taking the link away would be a
 * regression on a page they already used"); the PO has now ruled the register
 * is accounting's own workspace and nobody else's nav.
 *
 * Deliberately NOT a capability change. `accounting.bills.view` keeps
 * ORG_ADMIN and PM_MANAGER because (a) the capability catalog's invariant is
 * that admin is the monotonic top of every capability, and (b) Admin/PM still
 * legitimately read the same endpoints from the disposition detail page (the
 * bills card and the owner-statement door, PO 2026-08-05) and the two task
 * pages. Only the PAGE — nav item and `/accounting` route — is seat-gated;
 * nothing they could see here is hidden from them elsewhere (`/invoices`,
 * dispositions, `financials.view_amounts`).
 *
 * Ops-with-access passes, mirroring the ops bypass every other gate has.
 */
export function canSeeAccountingBillsPage(
  role: string | undefined,
  opsWithAccess = false,
): boolean {
  return opsWithAccess || role === 'ACCOUNTING';
}

/**
 * Paths Accounting may open. Everything else redirects to My Queue.
 * Account settings stay reachable from the avatar menu.
 */
export function isAccountingAllowedPath(
  pathname: string,
  search = '',
): boolean {
  if (pathname === '/my-queue' || pathname.startsWith('/my-queue/')) return true;
  // Their own register. Exact match, so it cannot swallow `/account` (the
  // profile page) and the order of these two lines does not matter.
  if (pathname === '/accounting') return true;
  if (pathname === '/account') return true;

  /**
   * The two task pages — one turn, one ordinary work order. Everything the
   * task needs is ON them, which is why the work-order and inspection pages
   * could be dropped rather than merely discouraged.
   */
  if (new RegExp(`^/accounting/turns/${UUID}$`, 'i').test(pathname)) return true;
  if (new RegExp(`^/accounting/work-orders/${UUID}$`, 'i').test(pathname)) return true;

  // The deposit letter — accounting's own document, start to close.
  if (new RegExp(`^/dispositions/${UUID}$`, 'i').test(pathname)) return true;

  // Notice task deep-link opens the list with a preselected id — require the
  // query so bare `/notices` (the register) stays closed.
  if (pathname === '/notices') {
    return new URLSearchParams(search).has('noticeId');
  }

  return false;
}

/**
 * Where a blocked path should land INSTEAD of My Queue, when accounting has a
 * page of their own for the same thing.
 *
 * ★ THIS EXISTS BECAUSE THE QUEUE STILL SENDS THEM TO `/work-orders/:id`.
 *
 * A department task raised on a bill carries a `workOrderId`, and the server
 * builds its deep-link from that (`taskEntityUrl` → `/work-orders/:id`). With
 * that path gone from the allow-list, pressing Open on the one task type
 * accounting exists to do would bounce them to My Queue — the task's own
 * button, going nowhere.
 *
 * So the path is re-pointed rather than merely refused: same bill, same
 * evidence, on the page built for this reader.
 *
 * ★ FIXED AT THE SOURCE 2026-08-04 — this is now a FALLBACK, not the fix.
 * `taskEntityUrl` emits the accounting path itself for a charging task, and
 * picks between `/accounting/turns/:id` and `/accounting/work-orders/:id` by
 * whether the work order sits on a turn (spec 142 A1) — which this function
 * cannot know from a URL alone. It maps every work order to the non-turn page,
 * so a TURN bill reached through here lands on a 404 that says its bills are
 * charged from the turn. That is honest but it is the worse answer, and it is
 * now only reachable from a stale link or an old cached payload.
 *
 * Keep it as a net; do not treat it as the routing rule.
 *
 * Returns `null` when there is no better destination — an inspection has no
 * accounting counterpart, and per the same ruling it should not get one.
 */
export function accountingRedirectFor(pathname: string): string | null {
  const wo = new RegExp(`^/work-orders/(${UUID})$`, 'i').exec(pathname);
  return wo?.[1] ? `/accounting/work-orders/${wo[1]}` : null;
}

/**
 * Which of the two Accounting-seat nav items is current.
 *
 * The layout used to pin every Accounting path to My Queue, so All bills
 * never highlighted — not on the register, and not on the task pages it
 * opens (`/accounting/work-orders/:id`, `/accounting/turns/:id`, a
 * deposit letter reached from a turn card). Those pages are the register's
 * own work, so they select All bills. My Queue stays selected only on the
 * queue itself.
 */
export function accountingNavSelectedKey(
  pathname: string,
): '/accounting' | '/my-queue' {
  if (pathname === '/accounting' || pathname.startsWith('/accounting/')) {
    return '/accounting';
  }
  if (new RegExp(`^/dispositions/${UUID}`, 'i').test(pathname)) {
    return '/accounting';
  }
  return '/my-queue';
}
